import React, {useEffect, useState} from "react";
import styles from '../styles/settings.module.css';
import Header from "../../../components/header/header";
import SettingsSubmenu from "../components/submenu/settings_submenu";
import SettingsSection from "../components/section/section";
import axiosInstance from "../../../AxiosInstance";
import Modal from "../../../components/modal/modal";
import {LogoInput, TextInput} from "../../../components/input/inputs";
import LimitExceededModal from "../../../components/limit_exceeded_modal/LimitExceededModal";
import {useTranslation} from "react-i18next";

export default function CompanySettings() {
    const {t} = useTranslation();
    const [companies, setCompanies] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [form, setForm] = useState({});
    const [canCreate, setCanCreate] = useState(false);

    // Limit exceeded modal state
    const [showLimitModal, setShowLimitModal] = useState(false);
    const [limitError, setLimitError] = useState({error: '', current: 0, max: 0, code: '', subscriptionStatus: null});

    useEffect(() => {
        axiosInstance.get(`company/`).then((res) => {
            if(!(res.data?.length > 0)) {
                const arr = [];
                arr.push(res.data);
                setCompanies(arr);
            } else {
                setCanCreate(true);
                setCompanies(res.data)
            }
        });
    }, []);

    const createCompany = () => {
        if (!form.name || !form.office) {
            alert(t('settings.companies.allRequired'));
            return;
        }

        let formData = new FormData();
        for(const key in form) {
            formData.append(key, form[key]);
        }

        axiosInstance.post('company/', formData).then(() => {
            window.location.reload();
        }).catch((err) => {
            // Handle 402 Payment Required - Limit exceeded
            if (err.response?.status === 402) {
                const data = err.response.data;
                setLimitError({
                    error: data.error || t('settings.companies.limitExceeded'),
                    current: data.current || 0,
                    max: data.max || 0,
                    code: data.code || '',
                    subscriptionStatus: data.status || null
                });
                setShowCreateModal(false);
                setShowLimitModal(true);
                return;
            }
            console.error('Error creating company:', err);
            alert(t('settings.companies.errorCreating'));
        });
    }

    const editCompany = () => {
        let formData = new FormData();
        for(const key in form) {
            formData.append(key, form[key]);
        }

        axiosInstance.put(`company/${form.id}/`, formData).then(() => {
            window.location.reload();
        }).catch((err) => {
            console.error('Error editing company:', err);
            alert(t('settings.companies.errorEditing'));
        });
    }

    const deleteCompany = () => {
        if (!selectedCompany) return;
        const currentCompanyId = parseInt(localStorage.getItem('company_id'));
        axiosInstance.delete(`company/${selectedCompany.id}/`).then(() => {
            if (selectedCompany.id === currentCompanyId) {
                localStorage.removeItem('company_id');
                window.location.href = '/';
            } else {
                window.location.reload();
            }
        });
    }

    return (
        <div className={styles.dashboard_container}>
            <Header/>

            <LimitExceededModal
                show={showLimitModal}
                setter={setShowLimitModal}
                error={limitError.error}
                current={limitError.current}
                max={limitError.max}
                type="companies"
                code={limitError.code}
                subscriptionStatus={limitError.subscriptionStatus}
            />

            <Modal show={showCreateModal} setter={setShowCreateModal} name={t('settings.companies.createCompany')} buttonName={t('settings.companies.createCompany')} onClick={createCompany}>
                <LogoInput name={t('hub.companyLogo')} internalName={'logo'} form={form} setForm={setForm} hideName={true}/>
                <TextInput name={t('settings.companies.companyName')} internalName={'name'} form={form} setForm={setForm} hideName={true} placeholder={t('settings.companies.companyName')}/>
                <TextInput name={t('offices.officeName')} internalName={'office'} form={form} setForm={setForm} hideName={true} placeholder={t('offices.officeName')}/>
            </Modal>

            <Modal show={showEditModal} setter={setShowEditModal} name={t('common.edit')} buttonName={t('common.save')} onClick={editCompany}>
                <LogoInput name={t('hub.companyLogo')} internalName={'logo'} form={form} setForm={setForm} hideName={true} current={selectedCompany?.image?.image}/>
                <TextInput name={t('settings.companies.companyName')} internalName={'name'} form={form} setForm={setForm} hideName={true} placeholder={t('settings.companies.companyName')}/>
            </Modal>

            <Modal show={showDeleteModal} setter={setShowDeleteModal} name={t('common.delete')} buttonName={t('common.delete')} onClick={deleteCompany}>
                <p>Are you sure you want to delete <strong>{selectedCompany?.name}</strong>? This action cannot be undone.</p>
            </Modal>

            <div className={styles.dashboardInnerContainer}>
                <SettingsSubmenu active={4}/>

                <SettingsSection title={t('settings.companies.title')} description={t('settings.companies.description')}>
                    <div side={'left'}>
                        {canCreate && <div className={styles.primaryButton} onClick={() => setShowCreateModal(true)}>{t('settings.companies.createCompany')}</div>}
                    </div>
                    <div side={'right'}>
                        <div className={`${styles.squareContainer} p-3`}>
                            {companies?.map((company) => (
                                <div key={company.id} className={`d-flex flex-row align-items-center mb-3 justify-content-between`}>
                                    <img alt={'company'} src={company.image ? company.image.image : '/mid_cloud.png'} width={'80px'} height={'80px'} style={{borderRadius: '8px', objectFit: 'contain'}}/>
                                    <div className={'d-flex flex-column ps-3'}>
                                        <div style={{color: "#334155", fontWeight: '600'}}>{company.name}</div>
                                        <div style={{color: "#64748B", fontSize: "14px"}}>{company.corporate_group.name}</div>
                                    </div>
                                    <div className={'d-flex flex-column ps-3'}>
                                        <div style={{color: "#334155", fontWeight: '600'}}>{t('settings.companies.offices')}</div>
                                        <div>{company.projects.length}</div>
                                    </div>
                                    <div className={'d-flex flex-row gap-2'}>
                                        <div onClick={() => {
                                            setSelectedCompany(company);
                                            setShowEditModal(true);
                                            setForm({id: company.id, name: company.name})
                                        }} className={`d-flex flex-column ${styles.editButton}`}/>
                                        <div onClick={() => {
                                            setSelectedCompany(company);
                                            setShowDeleteModal(true);
                                        }} className={`d-flex flex-column ${styles.deleteButton}`}/>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </SettingsSection>
            </div>
        </div>
    );
}
